import { apiClient } from "./browser";
import { eventPath, type EventBinding, type EventSchemas } from "./events";
import type { CommandOptions } from "./suppliers";

export type BidSchemas = EventSchemas;
export type BidDossier = BidSchemas["SupplierBidDossier"];
export type BidContent = BidSchemas["BidContent"];
export type BidTarget = BidSchemas["BidTarget"];
export type BidResult = BidSchemas["BidCommandResult"];
export type BidReply = BidResult | BidSchemas["EventCloseResult"];
export type BidInput =
  | BidSchemas["BidStartInput"]
  | BidSchemas["BidSaveInput"]
  | BidSchemas["BidAdoptInput"]
  | BidSchemas["BidSubmitInput"]
  | BidSchemas["BidLifecycleInput"]
  | BidSchemas["BidDocumentInput"];

export function bidPath(binding: EventBinding, eventId: string, suffix = "") {
  return binding.audience === "supplier"
    ? (`${eventPath(binding, eventId)}/bid${suffix}` as const)
    : (`/tenants/${encodeURIComponent(binding.tenantId)}/sourcing/${encodeURIComponent(eventId)}/bids${suffix}` as const);
}
export function bidQuery<T>(
  binding: EventBinding,
  eventId: string,
  suffix: string,
  signal: AbortSignal,
) {
  return apiClient.request<T>({
    operation: "ReadBidDossier",
    method: "GET",
    kind: "query",
    path: bidPath(binding, eventId, suffix),
    signal,
  });
}
export function bidCommand(
  binding: EventBinding,
  eventId: string,
  suffix: string,
  options: CommandOptions,
  body: BidInput,
) {
  return apiClient.request<BidResult, BidInput>({
    operation: "WriteCompanyBid",
    method: "POST",
    kind: "command",
    path: bidPath(binding, eventId, suffix),
    body,
    signal: options.signal,
    antiforgeryToken: options.csrfToken,
    idempotencyKey: options.idempotencyKey,
  });
}
export function closeEvent(
  binding: EventBinding,
  eventId: string,
  options: CommandOptions,
  body: BidSchemas["EventCloseInput"],
) {
  return apiClient.request<BidSchemas["EventCloseResult"], typeof body>({
    operation: "CloseEventResponseWindow",
    method: "POST",
    kind: "command",
    path: eventPath(binding, eventId, "/close"),
    body,
    signal: options.signal,
    antiforgeryToken: options.csrfToken,
    idempotencyKey: options.idempotencyKey,
  });
}
export function bidTarget(data: BidDossier): BidTarget {
  return {
    grantId: data.grantId,
    revisionId: data.revisionId,
    windowVersion: data.window.version,
    expectedVersion: data.draft?.version ?? 0,
  };
}
